import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Upload, FileText, Globe, Building } from 'lucide-react'

const charterSchema = z.object({
  full_legal_name:  z.string().optional(),
  mandate:          z.string().optional(),
  founding_date:    z.string().optional(),
  jurisdiction:     z.string().optional(),
  legal_basis:      z.string().optional(),
  hq_address:       z.string().optional(),
  hq_city:          z.string().optional(),
  hq_country:       z.string().optional(),
  website_url:      z.string().url().optional().or(z.literal('')),
  official_email:   z.string().email().optional().or(z.literal('')),
  twitter_handle:   z.string().optional(),
  linkedin_url:     z.string().optional(),
  facebook_url:     z.string().optional(),
  instagram_handle: z.string().optional(),
})

type CharterFormData = z.infer<typeof charterSchema>

export function OrgCharterView({
  orgId,
  orgName,
  canEdit,
}: {
  orgId: string
  orgName: string
  canEdit: boolean
}) {
  const queryClient = useQueryClient()

  const { data: charter } = useQuery({
    queryKey: ['org-charter', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data } = await (supabase
        .from('org_charter' as any)
        .select('*')
        .eq('org_id', orgId)
        .maybeSingle() as any)
      return data as any | null
    },
  })

  const form = useForm<CharterFormData>({
    resolver: zodResolver(charterSchema),
    values: charter ?? {},
  })

  const mutation = useMutation({
    mutationFn: async (data: CharterFormData) => {
      const { data: { user } } = await supabase.auth.getUser()
      await (supabase
        .from('org_charter' as any)
        .upsert(
          {
            org_id:          orgId,
            ...data,
            last_updated_by: user?.id,
            updated_at:      new Date().toISOString(),
          },
          { onConflict: 'org_id' }
        ) as any)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-charter', orgId] })
    },
  })

  const uploadDocument = async (
    file: File,
    type: 'charter' | 'organogram' | 'mandate' | 'logo'
  ) => {
    const bucket = type === 'logo' ? 'org-logos' : 'org-documents'
    const ext    = file.name.split('.').pop()
    const path   = `${orgId}/${type}-${Date.now()}.${ext}`

    const { error } = await supabase.storage
      .from(bucket)
      .upload(path, file, { upsert: true })
    if (error) throw error

    const { data: urlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(path)

    await (supabase
      .from('org_charter' as any)
      .upsert(
        { org_id: orgId, [`${type}_url`]: urlData.publicUrl },
        { onConflict: 'org_id' }
      ) as any)

    queryClient.invalidateQueries({ queryKey: ['org-charter', orgId] })

    if (type === 'organogram' || type === 'charter') {
      await (supabase
        .from('org_onboarding' as any)
        .upsert({ org_id: orgId, charter_uploaded: true }, { onConflict: 'org_id' }) as any)
      queryClient.invalidateQueries({ queryKey: ['admin-institution-orgs'] })
      queryClient.invalidateQueries({ queryKey: ['admin-onboarding-progress'] })
    }
  }

  return (
    <div className="space-y-6">
      {!charter?.is_complete && (
        <div className="bg-[#FAEEDA] border border-amber-200 rounded-lg p-3 text-[12px] text-[#854F0B] text-justify">
          Complete this charter to help CONORBIT and your team understand your
          organisation's mandate and legal basis.
        </div>
      )}

      <form onSubmit={form.handleSubmit(d => mutation.mutate(d))} className="space-y-5">

        {/* Legal identity */}
        <section>
          <h3 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2">
            <Building size={13} />
            Legal Identity
          </h3>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1">
              <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                Full Legal Name
              </label>
              <input
                {...form.register('full_legal_name')}
                disabled={!canEdit}
                placeholder={`e.g. ${orgName}, full constitutional name`}
                className="w-full px-3 py-2 border border-border rounded-lg text-[13px] bg-background focus:outline-none focus:border-[#006B3C] disabled:opacity-60"
              />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                Institutional Mandate
              </label>
              <textarea
                {...form.register('mandate')}
                disabled={!canEdit}
                rows={3}
                placeholder="The constitutional or legal mandate of this institution…"
                className="w-full px-3 py-2 border border-border rounded-lg text-[13px] bg-background focus:outline-none focus:border-[#006B3C] resize-none text-justify disabled:opacity-60"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  Founding Date
                </label>
                <input
                  {...form.register('founding_date')}
                  type="date"
                  disabled={!canEdit}
                  className="w-full px-3 py-2 border border-border rounded-lg text-[13px] bg-background focus:outline-none focus:border-[#006B3C] disabled:opacity-60"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  Jurisdiction
                </label>
                <input
                  {...form.register('jurisdiction')}
                  disabled={!canEdit}
                  placeholder="e.g. ECOWAS region"
                  className="w-full px-3 py-2 border border-border rounded-lg text-[13px] bg-background focus:outline-none focus:border-[#006B3C] disabled:opacity-60"
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                Legal Basis
              </label>
              <input
                {...form.register('legal_basis')}
                disabled={!canEdit}
                placeholder="e.g. ECOWAS Treaty 1975, Protocol A/P1/1/95"
                className="w-full px-3 py-2 border border-border rounded-lg text-[13px] bg-background focus:outline-none focus:border-[#006B3C] disabled:opacity-60"
              />
            </div>
          </div>
        </section>

        {/* Contact & Presence */}
        <section>
          <h3 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2">
            <Globe size={13} />
            Contact &amp; Presence
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'hq_city',        label: 'Headquarters City' },
              { key: 'hq_country',     label: 'Country' },
              { key: 'official_email', label: 'Official Email' },
              { key: 'website_url',    label: 'Website URL' },
              { key: 'twitter_handle', label: 'Twitter / X Handle' },
              { key: 'linkedin_url',   label: 'LinkedIn URL' },
            ].map(({ key, label }) => (
              <div key={key} className="space-y-1">
                <label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  {label}
                </label>
                <input
                  {...form.register(key as keyof CharterFormData)}
                  disabled={!canEdit}
                  className="w-full px-3 py-2 border border-border rounded-lg text-[13px] bg-background focus:outline-none focus:border-[#006B3C] disabled:opacity-60"
                />
              </div>
            ))}
          </div>
        </section>

        {/* Official Documents */}
        <section>
          <h3 className="text-[12px] font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-2">
            <FileText size={13} />
            Official Documents
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {([
              { type: 'charter',    label: 'Founding Charter / Constitution', accept: '.pdf' },
              { type: 'mandate',    label: 'Official Mandate Document',       accept: '.pdf' },
              { type: 'organogram', label: 'Organisational Chart',            accept: '.pdf,.png,.jpg' },
              { type: 'logo',       label: 'Official Logo',                   accept: '.png,.jpg,.svg' },
            ] as const).map(({ type, label, accept }) => {
              const existingUrl = charter?.[`${type}_url`] as string | undefined
              return (
                <div key={type} className="border border-border rounded-lg p-3 space-y-2">
                  <div className="text-[11.5px] font-medium">{label}</div>
                  {existingUrl ? (
                    <div className="flex items-center gap-2">
                      <a
                        href={existingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[11px] text-[#006B3C] hover:underline flex items-center gap-1"
                      >
                        <FileText size={11} /> View document
                      </a>
                      {canEdit && (
                        <span className="text-[10px] text-muted-foreground">· Replace below</span>
                      )}
                    </div>
                  ) : (
                    <div className="text-[11px] text-muted-foreground">Not uploaded</div>
                  )}
                  {canEdit && (
                    <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground cursor-pointer transition-colors">
                      <Upload size={11} />
                      Upload {type}
                      <input
                        type="file"
                        accept={accept}
                        className="hidden"
                        onChange={async e => {
                          const file = e.target.files?.[0]
                          if (file) await uploadDocument(file, type)
                        }}
                      />
                    </label>
                  )}
                </div>
              )
            })}
          </div>
        </section>

        {canEdit && (
          <button
            type="submit"
            disabled={mutation.isPending}
            className="w-full py-2.5 bg-[#006B3C] text-white rounded-lg text-[13px] font-medium hover:bg-[#005A32] disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : 'Save Charter'}
          </button>
        )}

        {mutation.isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-[12px] text-red-700">
            {(mutation.error as Error).message}
          </div>
        )}
      </form>
    </div>
  )
}
